import _ from 'lodash';
import { HblPoNo } from '../../../../fms.core/HblPoNo';
import { OhContainer } from '../../../../fms.core/OhContainer';
import { OiHbl } from '../../../../fms.core/OiHbl';
import { OiMbl } from '../../../../fms.core/OiMbl';
import { formatAmount } from '../../../../util';

export function mblToHblModel(mbl:OiMbl) {
  if (!mbl) {
    return {};
  } else {
    return {
      mbl,
      moveType: mbl.moveType,
      agentRefNo: mbl.agentRefNo,
      shipmentType: mbl.shipmentType,
      finalDestination: mbl.finalDest,
      feta: mbl.fEta,
      classOfEntry: mbl.itClass,
      entryDate: mbl.itDate,
      entryPlace: mbl.itPlace,
      entryNo: mbl.itNo,
      containerInfoLineItems: mbl.oimContainerList
        ? mbl.oimContainerList.map(l => ({
          id: l.id,
          containerNo: l.containerNo,
          type: l.conType,
          unit: l.pUnit,
          pkgs: l.pkgs,
          agentShow: false,
        }))
        : [],
    }
  }
}

export function mergeMblToHblModel(mbl:OiMbl, hbl:OiHbl) {
  if (!mbl) {
    return;
  }

  let containerIndexesToRemove = [];
  let mblCi = mbl.oimContainerList ? mbl.oimContainerList : [];

  if (hbl.container) {
    // First, delete those line items for which container no longer exists in MBL
    for (let i = hbl.container.length - 1; i >= 0; --i) {
      if (!mblCi.some(x => x.id === hbl.container[i].id)) {
        containerIndexesToRemove.push(i);
      }
    }

    containerIndexesToRemove.forEach(i => hbl.container.splice(i, 1));

    // For remaining items map latest data from MBL
    for (let i = 0; i < hbl.container.length; ++i) {
      const match = mblCi.find(x => x.id === hbl.container[i].id);
      if (!match) {
        continue;
      }

      hbl.container[i].containerNo = match.containerNo;
      hbl.container[i].conType = match.conType;
      hbl.container[i].mUnit = match.pUnit;
    }

    // As a last step, check for new MBL items, not yet in HBL and add them
    for (let i = 0; i < mblCi.length; ++i) {
      if (!hbl.container.some(x => x.id === mblCi[i].id)) {
        hbl.container.push(new OhContainer({          
          mCntId: mblCi[i].id,
          containerNo: mblCi[i].containerNo,
          conType: mblCi[i].conType,
          mUnit: mblCi[i].pUnit,
          mPkgs: mblCi[i].pkgs,
          select: false,
        }));
      }

    }
  }

  // Now remove line items of po info containers that no longer exist in MBL
  if (hbl.other) {
    let poIndexesToRemove = []
    for (let i = hbl.other.length - 1; i>= 0; --i) {
      if (!mblCi.some(x => x.id.toString() === hbl.other[i].containerNo)) {
        poIndexesToRemove.push(i);
      }
    }

    poIndexesToRemove.forEach(i => hbl.other.splice(i, 1));
  }

  hbl.oimblId = mbl.id;
  hbl.agentRefNo = mbl?.agentRefNo ?? '';
}

export interface PoCheckResultCoItem {
  mCntId: number,
  containerNo: string,
  valid: boolean,
  requiredAmount: number,
  notAppliedAmount: number,
  poElements: number,
};
export interface PoCheckResult {
  valid: boolean,
  coItems: Array<PoCheckResultCoItem>,
  poItems: Array<HblPoNo>,
};

export function checkPoInfoLineItems(containerInfoLineItems:Array<OhContainer>, poInfoLineItems:Array<HblPoNo>):PoCheckResult {
  const co = containerInfoLineItems.filter(x => x.agentShow) || [];

  const result:PoCheckResult = {
    valid: true,
    coItems: co.map(x => ({
      mCntId: x.mCntId,
      containerNo: x.containerNo,
      valid: true,
      requiredAmount: x.pkgs || 0,
      notAppliedAmount: x.pkgs || 0,
      poElements: 0,
    })),
    poItems: poInfoLineItems,
  };

  for (let i = 0; i < result.poItems.length; ++i) {
    const po = result.poItems[i];
    if (!po.cntrLinkId) {
      // Container not selected for this line item
      continue;
    }
    const coIndex = result.coItems.findIndex(x => x.mCntId === po.cntrLinkId);
    if (coIndex < 0) {
      // Should never happen
      continue;
    }

    result.coItems[coIndex].notAppliedAmount = result.coItems[coIndex].notAppliedAmount - (po.shipQty ?? 0);
    result.coItems[coIndex].poElements = result.coItems[coIndex].poElements + 1;
  }

  for (let i = 0; i < result.coItems.length; ++i) {
    // If total sum of amounts entered is above total pkgs - it is not valid
    const totalsQty = poInfoLineItems.reduce((sum, it) => sum + (it.containerNo === result.coItems[i].containerNo ? it.shipQty : 0), 0);
    if ((result.coItems[i].requiredAmount !== totalsQty) && result.coItems[i].poElements > 0) {
      result.coItems[i].valid = false;
      result.valid = false;
    }
  }

  if (!result.valid) {
    // If not valid, for each container item find the first in po, apply whole amount to it and reset containers for others
    for (let i = 0; i < result.coItems.length; ++i) {
      if (result.coItems[i].valid) {
        continue;
      }

      if (result.coItems[i].notAppliedAmount > 0) {
        // Qty is missing. Apply it to the last matching item
        for (let j = result.poItems.length - 1; j >= 0; --j) {
          if (result.poItems[j].cntrLinkId === result.coItems[i].mCntId) {
            let curQty = result.poItems[j].shipQty || 0;
            result.poItems[j].shipQty = curQty + result.coItems[i].notAppliedAmount;
            break;
          }
        }
      } else {
        // There is extra qty applied. Start reducing from last item appropriately

        for (let j = result.poItems.length - 1; j >= 0; --j) {
          if (result.coItems[i].notAppliedAmount === 0) {
            break;
          }

          if (result.poItems[j].cntrLinkId === result.coItems[i].mCntId) {
            let curQty = result.poItems[j].shipQty || 0;
            const delta = Math.min(curQty, -result.coItems[i].notAppliedAmount);
            result.poItems[j].shipQty = curQty - delta;
            result.coItems[i].notAppliedAmount = result.coItems[i].notAppliedAmount + delta;
          }
        }
      }
    }
  }

  return result;
}

export function createDefaultPonoLineOih(h: OiHbl) {
  const po = new HblPoNo({
    hblId: h.id,
    poNo: 'N/A',
    skuNo: '',
    htsCode: '',
    coo: '',
    description: !_.isEmpty(h.commodity) && (h.commodity.length ?? 0) > 50 ? h.commodity.substring(0, 50) : h.commodity ?? '',
    shipQty: h.pkgs,
    shipUnit: h.punit,
    uPrice: 0,
    kgs: 0,
    lbs: 0,
    cbm: 0,
    cft: 0,
    cntrLinkId: 0,
    pimsPoHdLinkId: 0,
    pimsPoDetLinkId: 0,
  });
  po.trackable = true;
  h.other.push(po);
}


export function calculateBodyContent(current:Partial<OiHbl>, change:Partial<OiHbl>, isIntnl:boolean): Partial<OiHbl> {
  // const c = _.has(change, 'container') ? change.container : current.container;
  const pkg = _.has(change, 'pkgs') ? change.pkgs : current.pkgs;
  const packagesUnit = _.has(change, 'punit') ? change.punit : current.punit;
  const cbm = _.has(change, 'cbm') ? change.cbm : current.cbm;
  const cft = _.has(change, 'cft') ? change.cft : current.cft;
  const kgs = _.has(change, 'kgs') ? change.kgs : current.kgs;
  const lbs = _.has(change, 'lbs') ? change.lbs : current.lbs;

  // const noOfPackages = c?.map(x => ({ pkgs: x.pkgs, agentShow: x.agentShow }))
  //   .filter(x => !!x && !!x.pkgs && x.agentShow).reduce((acc, x) => acc + (x.pkgs ?? 0), 0);
  const numberOfPackages = `${formatAmount(pkg??0, 0)} ${packagesUnit}`;
  let tempMarkPkg='';
  if(current.lclFcl === 'F'){
    tempMarkPkg = numberOfPackages + '\r\n' + current.containerSummary;
  }

  const grossWeight = `${formatAmount(kgs, 2)} KGS` + (isIntnl ? '' : `\r\n${formatAmount(lbs, 2)} LBS`);

  const measurment = `${formatAmount(cbm, 3)} CBM` + (isIntnl ? '' : `\r\n${formatAmount(cft, 0)} CFT`);

  return {
    markPkg: _.isEmpty(tempMarkPkg) ? numberOfPackages: tempMarkPkg,
    markGross: grossWeight,
    markMeasure: measurment,
  };
}

export function formatHblType(h:string|null) {
  if (h === 'S') {
    return 'Standard';

  } else if (h === 'C') {
    return 'Co-Load';
  }

  return 'None';
}